import { Leaf, Plus } from 'lucide-react';
import { useState } from 'react';

import type { TreeSpecies, TreeSpeciesInput } from '../../api/tree-species';
import { usePermissions } from '../../hooks/usePermissions';
import { Permission } from '../../types/permissions';

import DeleteTreeSpeciesModal from './components/DeleteTreeSpeciesModal';
import TreeSpeciesFormModal from './components/TreeSpeciesFormModal';
import TreeSpeciesList from './components/TreeSpeciesList';
import { useTreeSpecies } from './use-tree-species';

type Editing = { species: TreeSpecies | null } | null;

export default function TreeSpeciesPage() {
  const { hasAnyPermission } = usePermissions();
  const canCreate = hasAnyPermission(Permission.CREATE_SETTINGS, Permission.WRITE_ALL);

  const { list, create, update, remove } = useTreeSpecies();
  const [editing, setEditing] = useState<Editing>(null);
  const [deleting, setDeleting] = useState<TreeSpecies | null>(null);
  const [error, setError] = useState<string | null>(null);
  const onError = (err: Error) => setError(err.message);

  const actions = {
    canEdit: hasAnyPermission(Permission.UPDATE_SETTINGS, Permission.WRITE_ALL),
    canDelete: hasAnyPermission(Permission.DELETE_SETTINGS, Permission.WRITE_ALL),
    busy: update.isPending || remove.isPending,
    onEdit: (s: TreeSpecies) => setEditing({ species: s }),
    onToggleActive: (s: TreeSpecies) => {
      setError(null);
      update.mutate({ id: s.id, patch: { isActive: !s.isActive } }, { onError });
    },
    onDelete: (s: TreeSpecies) => {
      setError(null);
      setDeleting(s);
    },
  };

  const save = (target: TreeSpecies | null) => (input: TreeSpeciesInput) =>
    target ? update.mutateAsync({ id: target.id, patch: input }) : create.mutateAsync(input);

  return (
    <div className="bg-white dark:bg-[#1e2521] rounded-xl border border-stone-200 dark:border-stone-700">
      <div className="flex items-center gap-2 px-5 py-4 border-b border-stone-200 dark:border-stone-700">
        <Leaf size={16} className="text-moss-600 dark:text-moss-400" />
        <h2 className="text-base font-semibold text-bark-900 dark:text-stone-100">Tree Species</h2>
        <span className="text-xs text-stone-400 ml-1 mr-auto">
          {list.data?.length ?? 0} species
        </span>
        {canCreate && (
          <button
            type="button"
            className="nb-btn nb-btn-primary"
            onClick={() => setEditing({ species: null })}
          >
            <Plus size={14} aria-hidden="true" /> Add species
          </button>
        )}
      </div>

      <div className="p-5">
        {error && (
          <div role="alert" className="mb-3 text-sm text-red-600 dark:text-red-400">
            {error}
          </div>
        )}
        <TreeSpeciesList list={list} actions={actions} />
      </div>

      {editing && (
        <TreeSpeciesFormModal
          species={editing.species}
          onClose={() => setEditing(null)}
          onSave={save(editing.species)}
        />
      )}
      {deleting && (
        <DeleteTreeSpeciesModal
          species={deleting}
          busy={remove.isPending}
          onCancel={() => setDeleting(null)}
          onConfirm={() =>
            remove.mutate(deleting.id, { onError, onSettled: () => setDeleting(null) })
          }
        />
      )}
    </div>
  );
}
